import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/presentation/components/common/Button';
import { Input } from '@/presentation/components/common/Input';
import { ModalShell } from '@/presentation/components/common/ModalShell/ModalShell';
import { useToast } from '@/presentation/contexts/ToastContext';
import { httpClient } from '@/infrastructure/http/client/httpClient';

type NotificationEmailModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

type NotificationSettingsResponse = { email: string | null; whatsapp: string | null };

const errorMessage = (err: unknown): string | undefined =>
  err && typeof err === 'object' && 'response' in err
    ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
    : undefined;

/** Email y WhatsApp personales donde el usuario recibe avisos del Panel (pagos informados, solicitudes, tickets). */
export const NotificationEmailModal: React.FC<NotificationEmailModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { success } = useToast();

  const [email, setEmail] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [savedWhatsapp, setSavedWhatsapp] = useState<string | null>(null);
  const [isTesting, setIsTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setError(null);
    setIsLoading(true);
    httpClient
      .get<NotificationSettingsResponse>('/auth/notification-email')
      .then((res) => {
        if (cancelled) return;
        setEmail(res.data.email ?? '');
        setSavedWhatsapp(res.data.whatsapp ?? null);
        setWhatsapp(res.data.whatsapp ? `+${res.data.whatsapp}` : '');
      })
      .catch((err) => {
        if (!cancelled) setError(errorMessage(err) || t('auth.notificationEmailLoadError'));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, t]);

  const save = async () => {
    setError(null);
    const cleanEmail = email.trim();
    const cleanWhatsapp = whatsapp.trim();
    if (cleanEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setError(t('auth.notificationEmailInvalid'));
      return;
    }
    const waDigits = cleanWhatsapp.replace(/\D/g, '');
    if (cleanWhatsapp && (waDigits.length < 8 || waDigits.length > 15)) {
      setError(t('auth.notificationWhatsAppInvalid'));
      return;
    }
    setIsSaving(true);
    try {
      await httpClient.put<NotificationSettingsResponse>('/auth/notification-email', {
        email: cleanEmail,
        whatsapp: cleanWhatsapp,
      });
      success(t('auth.notificationEmailSaved'));
      onClose();
    } catch (err) {
      setError(errorMessage(err) || t('auth.notificationEmailSaveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const sendTest = async () => {
    setError(null);
    if (!savedWhatsapp || whatsapp.replace(/\D/g, '') !== savedWhatsapp) {
      setError(t('auth.notificationWhatsAppTestNeedsSave'));
      return;
    }
    setIsTesting(true);
    try {
      await httpClient.post('/auth/notification-whatsapp/test');
      success(t('auth.notificationWhatsAppTestSent'));
    } catch (err) {
      setError(errorMessage(err) || t('auth.notificationWhatsAppTestError'));
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={t('auth.notificationEmail')}
      subtitle={t('auth.notificationEmailHint')}
      size="sm"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
        className="space-y-4"
      >
        <Input
          label={t('auth.notificationEmailLabel')}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="nombre@ejemplo.com"
          autoComplete="email"
          disabled={isLoading}
        />
        <div className="space-y-1">
          <div className="flex items-end gap-2">
            <div className="min-w-0 flex-1">
              <Input
                label={t('auth.notificationWhatsAppLabel')}
                type="tel"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
                placeholder="+54 9 11 1234 5678"
                autoComplete="tel"
                disabled={isLoading}
              />
            </div>
            {savedWhatsapp ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                isLoading={isTesting}
                disabled={isLoading || isSaving}
                onClick={() => void sendTest()}
              >
                {t('auth.notificationWhatsAppTest')}
              </Button>
            ) : null}
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">{t('auth.notificationWhatsAppHint')}</p>
        </div>
        <ul className="list-disc space-y-0.5 pl-5 text-xs text-slate-500 dark:text-slate-400">
          <li>{t('auth.notificationEmailEventPayment')}</li>
          <li>{t('auth.notificationEmailEventSignup')}</li>
          <li>{t('auth.notificationEmailEventDemo')}</li>
          <li>{t('auth.notificationEmailEventTicket')}</li>
        </ul>
        <p className="text-xs text-slate-500 dark:text-slate-400">{t('auth.notificationWhatsAppPaymentDigest')}</p>
        {error ? <p className="text-sm text-red-600 dark:text-red-400">{error}</p> : null}
        <div className="flex flex-wrap justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" size="sm" isLoading={isSaving} disabled={isLoading}>
            {t('common.save')}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
};
